import { Component, Inject, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CargoDialogResult } from '@cargo/models/cargo-dialog-result';
import { CargoDialogService } from '@cargo/services/cargo-dialog.service';
import { AppConfigService } from '@core/services/app-config.service';
import { Logger } from '@core/services/logger.service';
import { MaterialPreviewDialogParams } from '@order/models/material-preview-dialog-params';
import { MaterialPreviewDialogResult } from '@order/models/material-preview-dialog-result';
import { FindMaterialDialogComponent } from '@order/order/find-material-dialog/find-material-dialog.component';
import { MaterialPreviewDialogComponent } from '@order/order/material-preview-dialog/material-preview-dialog.component';
import { SubSink } from 'subsink';

@Component({
	selector: 'cass-new-material-dialog',
	templateUrl: './new-material-dialog.component.html',
	styleUrls: ['./new-material-dialog.component.css']
})
export class NewMaterialDialogComponent implements OnDestroy {
	private readonly COMPONENT_NAME = "NewMaterialDialogComponent";
	private _subs = new SubSink();

	protected isCargoEnabled: boolean = false;

	constructor (
		@Inject(MAT_DIALOG_DATA) private _materialPreviewParams: MaterialPreviewDialogParams,
		private _selfDialogRef: MatDialogRef<NewMaterialDialogComponent>,
		private _appConfigService: AppConfigService,
		private _cargoDialogService: CargoDialogService,
		private _dialog: MatDialog,
		private _logger: Logger
	) {
		this._subs.add(
			this._appConfigService.isCargoEnabled$.subscribe((isCargoEnabled: boolean) => {
				if (isCargoEnabled) {
					this.isCargoEnabled = true;
					this._logger.debug(this.COMPONENT_NAME, "Init with Cargo enabled");
				}
				else {
					this._logger.debug(this.COMPONENT_NAME, "Init with Cargo disabled");
				}
			}));
	}

	ngOnDestroy(): void {
		this._subs.unsubscribe();
	}

	uploadCompleteMaterial(): void {
		this._selfDialogRef.close();

		if (this.isCargoEnabled && this._materialPreviewParams.orderAdDetail.orderId) {
			// CASS-453 & CASS-472 - use parent order id as order id when interfacing with Cargo
			this._cargoDialogService.displayUploadReadyMaterialDialog(
				"ORDER_BASKET.NEW_MATERIAL_OPTIONS_DIALOG.UPLOAD_COMPLETE_MATERIAL_OPTION",
				this._materialPreviewParams.orderAdDetail.orderId,
				this._materialPreviewParams.orderAdDetail.materialId,
				this._materialPreviewParams.orderAdDetail.width,
				this._materialPreviewParams.orderAdDetail.depth
			)
				.subscribe({
					next: (cargoDialogResult: CargoDialogResult | undefined) => {
						// undefined means the dialog was cancelled
						if (cargoDialogResult && cargoDialogResult.previewURL) {
							if (this._materialPreviewParams && this._materialPreviewParams.newMaterialUploadedReporter) {
								const materialPreviewDialogResult = new MaterialPreviewDialogResult();
								materialPreviewDialogResult.previewUrl = cargoDialogResult.previewURL;

								this._materialPreviewParams.newMaterialUploadedReporter.next(materialPreviewDialogResult);

								// no new uploads are expected, close the reporter
								this._materialPreviewParams.newMaterialUploadedReporter.complete();
							}
						}
					}
				});
		}
		else {
			this.displayStandardMaterialPreviewDialog();
		}
	}

	requestProduction(): void {
		this._selfDialogRef.close();

		// CASS-453 & CASS-472 - use parent order id as order id when interfacing with Cargo
		if (!this._materialPreviewParams.orderAdDetail.orderId) {
			this._logger.error(this.COMPONENT_NAME, "requestProduction(): the order Id in the provided order line is empty");
			return;
		}

		this._cargoDialogService.displayProductionRequestDialog(
			"ORDER_BASKET.NEW_MATERIAL_OPTIONS_DIALOG.WE_WILL_DESIGN_IT_OPTION",
			this._materialPreviewParams.orderAdDetail.orderId, this._materialPreviewParams.orderAdDetail.materialId, this._materialPreviewParams.orderAdDetail.width, this._materialPreviewParams.orderAdDetail.depth)
			.subscribe({
				next: (cargoDialogResult: CargoDialogResult | undefined) => {
					if (cargoDialogResult) {
						if (this._materialPreviewParams && this._materialPreviewParams.newMaterialUploadedReporter) {
							this._materialPreviewParams.newMaterialUploadedReporter.next(new MaterialPreviewDialogResult());

							// no new uploads are expected, close the reporter
							this._materialPreviewParams.newMaterialUploadedReporter.complete();
						}
					}
				}
			});
	}

	repeatPastAd(): void {
		this._selfDialogRef.close();

		if (this._materialPreviewParams) {
			this._dialog.open(FindMaterialDialogComponent, {
				data: this._materialPreviewParams,
				width: '80vw'
			});
		}
	}

	private displayStandardMaterialPreviewDialog(): void {
		this._dialog.open(MaterialPreviewDialogComponent,
			{
				data: this._materialPreviewParams,
				maxWidth: '80vw',
			});
	}
}
